/**
 * useCopyJournalToWorkspace - Deep clone a shared journal to user's workspace
 * Copies all blocks including PDFs to new storage paths
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Copy a PDF file to a new storage path
 */
async function copyPDFFile(sourcePath: string, newJournalId: string): Promise<string> {
    try {
        // Download the source file
        const { data: fileData, error: downloadError } = await supabase.storage
            .from('original-pdfs')
            .download(sourcePath);

        if (downloadError || !fileData) {
            console.error('Failed to download PDF:', downloadError);
            throw new Error('Failed to copy PDF file');
        }

        // Extract filename from source path
        const filename = sourcePath.split('/').pop() || 'document.pdf';
        const newPath = `${newJournalId}/${filename}`;

        // Upload to new path
        const { error: uploadError } = await supabase.storage
            .from('original-pdfs')
            .upload(newPath, fileData, {
                contentType: 'application/pdf',
                upsert: false,
            });

        if (uploadError) {
            console.error('Failed to upload PDF:', uploadError);
            throw new Error('Failed to copy PDF file');
        }

        return newPath;
    } catch (error) {
        console.error('PDF copy error:', error);
        throw error;
    }
}

export const useCopyJournalToWorkspace = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationFn: async (sourceJournalId: string) => {
            if (!user) throw new Error("Must be logged in to copy journal");

            // 1. Fetch the source journal
            const { data: sourceJournal, error: journalError } = await supabase
                .from('journals')
                .select('*')
                .eq('id', sourceJournalId)
                .eq('sharing_mode', 'community')
                .single();

            if (journalError || !sourceJournal) {
                throw new Error("Journal not found or not shared");
            }

            // 2. Create new journal (without sharing fields)
            // Reset all todo items to incomplete
            const resetTodos = sourceJournal.todo_items
                ? (sourceJournal.todo_items as any[]).map((todo: any) => ({
                    ...todo,
                    completed: false,
                    completedAt: undefined,
                }))
                : null;

            const { data: newJournal, error: createError } = await supabase
                .from('journals')
                .insert({
                    user_id: user.id,
                    name: `${sourceJournal.name} (Copy)`,
                    description: sourceJournal.description,
                    summary: sourceJournal.summary,
                    tutor_instructions: sourceJournal.tutor_instructions,
                    todo_items: resetTodos,
                    // NOT copying: sharing_mode, share_token, shared_at, view_count, copy_count, etc.
                })
                .select()
                .single();

            if (createError || !newJournal) {
                throw new Error("Failed to create journal copy");
            }

            // 3. Fetch source blocks
            const { data: sourceBlocks, error: blocksError } = await supabase
                .from('journal_blocks')
                .select('*')
                .eq('journal_id', sourceJournalId)
                .order('position');

            if (blocksError) {
                throw new Error("Failed to fetch journal blocks");
            }

            // 4. Deep copy blocks (with new IDs, handle PDFs)
            if (sourceBlocks && sourceBlocks.length > 0) {
                const blockInserts = await Promise.all(
                    sourceBlocks.map(async (block) => {
                        let content = block.content;

                        // For PDF blocks, copy the actual file to new storage path
                        if (block.block_type === 'pdf' && content) {
                            const pdfContent = content as { storagePath?: string };
                            if (pdfContent.storagePath) {
                                try {
                                    const newPath = await copyPDFFile(pdfContent.storagePath, newJournal.id);
                                    content = {
                                        ...pdfContent,
                                        storagePath: newPath,
                                    };
                                } catch (error) {
                                    console.warn('Failed to copy PDF, keeping reference:', error);
                                    // Keep original path as fallback (user won't be able to edit but can view)
                                }
                            }
                        }

                        return {
                            journal_id: newJournal.id,
                            block_type: block.block_type,
                            title: block.title,
                            position: block.position,
                            content: content,
                            summary: block.summary,
                            key_points: block.key_points,
                            tutor_context: block.tutor_context,
                            is_collapsed: false, // Start expanded in new journal
                        };
                    })
                );

                const { error: insertError } = await supabase
                    .from('journal_blocks')
                    .insert(blockInserts);

                if (insertError) {
                    console.error('Failed to copy blocks:', insertError);
                    // Don't throw - partial copy is better than nothing
                }
            }

            // 5. Increment copy count on source (non-blocking via edge function)
            supabase.functions
                .invoke('journal-sharing-metrics', {
                    body: { journalId: sourceJournalId, action: 'copy' },
                })
                .catch((err) => console.warn('Failed to increment copy count:', err));

            return newJournal;
        },
        onSuccess: (newJournal) => {
            queryClient.invalidateQueries({ queryKey: ['journals'] });
            queryClient.invalidateQueries({ queryKey: ['community-journals'] });
            toast.success("Journal copied to your workspace! 📚");
            navigate(`/journals/${newJournal.id}`);
        },
        onError: (error) => {
            console.error("Copy error:", error);
            toast.error("Failed to copy journal. Please try again.");
        },
    });
};
